import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { errorMessage } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import Swal from 'sweetalert2'
import GroupsMessageModal from './GroupsMessageModal'
import GroupsOverviewTable, { isPickable } from './GroupsOverviewTable'
import PaperCriteriasForm from './PaperCriteriasForm'
import StudentsScoresTable from './StudentsScoresTable'

/** `?edit=criterias` swaps the score table for the paper's criteria form, as legacy's own URL does. */
const EDIT_CRITERIAS = 'criterias'

export default function AdministratorCourseScoresPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('subPage:scores')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const canManageScores = useAdminAccess().canManage('courseScores')
  const headers = useAuthenticatedHeaders()
  const { courseId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSemester = useActiveSemester()

  const semIdStr = searchParams.get('semId') ?? (activeSemester?.id != null ? String(activeSemester.id) : '')
  const parsedSemId = semIdStr !== '' ? parseInt(semIdStr, 10) : null
  const groupIdStr = searchParams.get('groupId')
  const selectedGroupId = groupIdStr != null && groupIdStr !== '' ? parseInt(groupIdStr, 10) : null
  const isEditingCriterias = searchParams.get('edit') === EDIT_CRITERIAS

  const [pickedGroupIds, setPickedGroupIds] = React.useState<readonly number[]>([])
  const [isExporting, setIsExporting] = React.useState(false)
  const [isMessageOpen, setIsMessageOpen] = React.useState(false)

  const { data: semesters } = useApi({
    endpoint: Api.getSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
    revalidateIfStale: false,
    suspense: false,
  })

  /**
   * A closed semester is the only place legacy asks for a service memo before it lets scores
   * change; the active and the upcoming ones do not.
   */
  const isClosedSemester = React.useMemo(() => {
    const semester = (semesters ?? []).find((item) => String(item.id) === String(parsedSemId))
    if (semester == null) return false
    return !semester.isActive && semester.isFuture !== true
  }, [parsedSemId, semesters])

  const {
    data: groups,
    isValidating: areGroupsPending,
    mutate: refetchGroups,
  } = useApi({
    endpoint: Api.getAdministratorCourseScores,
    params: React.useMemo(
      () => ({ headers, args: { id: courseId! }, query: { semId: parsedSemId ?? 0 } }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null && parsedSemId != null,
    suspense: false,
  })

  const { data: courseGroups } = useApi({
    endpoint: Api.getAdministratorCourseGroups,
    params: React.useMemo(
      () => ({ headers, args: { id: courseId! }, query: { semId: parsedSemId ?? 0 } }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null && parsedSemId != null,
    suspense: false,
  })

  const groupDetails = React.useMemo(() => {
    const map = new Map<number, Api.AdministratorCourseGroupItem>()
    for (const group of courseGroups ?? []) map.set(Number(group.id), group)
    return map
  }, [courseGroups])

  const {
    data: groupScores,
    isValidating: areScoresPending,
    mutate: refetchGroupScores,
  } = useApi({
    endpoint: Api.getAdministratorCourseGroupScores,
    params: React.useMemo(
      () => ({ headers, args: { id: courseId!, groupId: selectedGroupId! }, query: { semId: parsedSemId ?? 0 } }),
      [courseId, headers, parsedSemId, selectedGroupId]
    ),
    shouldFetch: courseId != null && parsedSemId != null && selectedGroupId != null,
    suspense: false,
  })

  const {
    data: criterias,
    isValidating: areCriteriasPending,
    mutate: refetchCriterias,
  } = useApi({
    endpoint: Api.getAdministratorCourseGroupCriterias,
    params: React.useMemo(
      () => ({ headers, args: { id: courseId!, groupId: selectedGroupId! }, query: { semId: parsedSemId ?? 0 } }),
      [courseId, headers, parsedSemId, selectedGroupId]
    ),
    shouldFetch: courseId != null && parsedSemId != null && selectedGroupId != null && isEditingCriterias,
    suspense: false,
  })

  // The audit trail only ever has rows for a closed semester, so don't ask for it otherwise.
  const { data: cards, mutate: refetchCards } = useApi({
    endpoint: Api.getAdministratorCourseGroupCards,
    params: React.useMemo(
      () => ({ headers, args: { id: courseId!, groupId: selectedGroupId! }, query: { semId: parsedSemId ?? 0 } }),
      [courseId, headers, parsedSemId, selectedGroupId]
    ),
    shouldFetch: courseId != null && parsedSemId != null && selectedGroupId != null && isClosedSemester,
    suspense: false,
  })

  const selectedGroup = React.useMemo(
    () => (groups ?? []).find((group) => group.id === selectedGroupId) ?? null,
    [groups, selectedGroupId]
  )

  const updateParams = React.useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        mutate(next)
        return next
      })
    },
    [setSearchParams]
  )

  const handleSelectGroup = React.useCallback(
    (groupId: number) => {
      setPickedGroupIds([])
      updateParams((next) => {
        next.delete('edit')
        if (next.get('groupId') === String(groupId)) {
          next.delete('groupId')
        } else {
          next.set('groupId', String(groupId))
        }
      })
    },
    [updateParams]
  )

  // Message recipients and criteria copy targets share the checkbox column, so picks never carry over.
  const openCriterias = React.useCallback(() => {
    setPickedGroupIds([])
    updateParams((next) => next.set('edit', EDIT_CRITERIAS))
  }, [updateParams])

  const closeCriterias = React.useCallback(() => {
    setPickedGroupIds([])
    updateParams((next) => next.delete('edit'))
  }, [updateParams])

  const togglePickedGroup = React.useCallback((groupId: number) => {
    setPickedGroupIds((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]))
  }, [])

  const pickAllGroups = React.useCallback(
    (checked: boolean) => {
      if (!checked) {
        setPickedGroupIds([])
        return
      }
      const pickable = isEditingCriterias
        ? (groups ?? []).filter((group) => isPickable(group, selectedGroupId))
        : (groups ?? [])
      setPickedGroupIds(pickable.map((group) => group.id))
    },
    [groups, isEditingCriterias, selectedGroupId]
  )

  const openMessage = React.useCallback(() => setIsMessageOpen(true), [])
  const closeMessage = React.useCallback(() => setIsMessageOpen(false), [])

  const handleMessageSent = React.useCallback(() => {
    setIsMessageOpen(false)
    setPickedGroupIds([])
  }, [])

  const refetchScores = React.useCallback(async () => {
    await Promise.all([refetchGroupScores(), refetchGroups(), refetchCards()])
  }, [refetchCards, refetchGroupScores, refetchGroups])

  const handleCriteriasSaved = React.useCallback(async () => {
    await Promise.all([refetchCriterias(), refetchGroupScores(), refetchGroups()])
    closeCriterias()
  }, [closeCriterias, refetchCriterias, refetchGroupScores, refetchGroups])

  const openExport = React.useCallback(
    async (load: () => Promise<Api.ExportFile>) => {
      if (isExporting) return
      setIsExporting(true)
      try {
        const result = await load()
        window.open(result.downloadUrl, '_blank', 'noopener,noreferrer')
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      } finally {
        setIsExporting(false)
      }
    },
    [isExporting]
  )

  const handleSummaryDownload = React.useCallback(() => {
    if (courseId == null || parsedSemId == null) return
    void openExport(
      async () =>
        await Api.getAdministratorCourseScoresSummaryExport({
          headers,
          args: { id: courseId },
          query: { semId: parsedSemId, mode: 'download' },
        })
    )
  }, [courseId, headers, openExport, parsedSemId])

  const handleSummaryPrint = React.useCallback(() => {
    if (courseId == null || parsedSemId == null) return
    void openExport(
      async () =>
        await Api.getAdministratorCourseScoresSummaryExport({
          headers,
          args: { id: courseId },
          query: { semId: parsedSemId, mode: 'print' },
        })
    )
  }, [courseId, headers, openExport, parsedSemId])

  const handleTogglePaperStatus = React.useCallback(async () => {
    if (courseId == null || parsedSemId == null || selectedGroupId == null) return
    const alert = await Swal.fire({
      title: t('uncategorized:change_status_confirmation'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('common:confirm'),
      cancelButtonText: t('common:cancel'),
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6C757D',
    })
    if (!alert.isConfirmed) return
    try {
      await Api.putAdministratorCourseGroupPaperStatus({
        headers,
        args: { id: courseId, groupId: selectedGroupId },
        query: { semId: parsedSemId },
      })
      await Promise.all([refetchGroups(), refetchGroupScores()])
      toast.success(t('course:paper_status_changed'))
    } catch (error) {
      toast.error(errorMessage(error as base.CommonError))
    }
  }, [courseId, headers, parsedSemId, refetchGroupScores, refetchGroups, selectedGroupId, t])

  const onTogglePaperStatus = React.useCallback(() => void handleTogglePaperStatus(), [handleTogglePaperStatus])

  if (courseId == null || parsedSemId == null) return <Loader className="m-auto flex" />
  if (groups == null && areGroupsPending) return <Loader className="m-auto flex" />
  if (groups == null || groups.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }

  const paper = selectedGroup?.paper ?? null

  return (
    <>
      <GroupsOverviewTable
        groups={groups}
        groupDetails={groupDetails}
        isPending={areGroupsPending && groups.length === 0}
        selectedGroupId={selectedGroupId}
        onSelectGroup={handleSelectGroup}
        isPickingGroups={isEditingCriterias}
        pickedGroupIds={pickedGroupIds}
        onTogglePickedGroup={togglePickedGroup}
        onPickAllGroups={pickAllGroups}
        onSendMessage={openMessage}
        onExport={handleSummaryDownload}
        onPrint={handleSummaryPrint}
        isExporting={isExporting}
      />

      <GroupsMessageModal
        headers={headers}
        courseId={courseId}
        semId={parsedSemId}
        groups={groups}
        recipientGroupIds={pickedGroupIds}
        isOpen={isMessageOpen}
        onClose={closeMessage}
        onSent={handleMessageSent}
      />

      {selectedGroup != null && (
        <>
          <div className="mb-2 mt-[15px] flex w-full flex-wrap justify-end gap-2">
            {!canManageScores ? null : isEditingCriterias ? (
              <Button variant="blue" onClick={closeCriterias}>
                {t('uncategorized:return_to_marks')}
              </Button>
            ) : (
              <Button variant="blue" onClick={openCriterias}>
                {t('uncategorized:manage_evaluation_sheet')}
              </Button>
            )}
          </div>

          {canManageScores && isEditingCriterias ? (
            <PaperCriteriasForm
              headers={headers}
              courseId={courseId}
              groupId={selectedGroup.id}
              semId={parsedSemId}
              data={criterias ?? null}
              isPending={criterias == null && areCriteriasPending}
              pickedGroupIds={pickedGroupIds}
              onSaved={handleCriteriasSaved}
              footerActions={
                paper != null && (
                  <Button variant="red" type="button" onClick={onTogglePaperStatus}>
                    {paper.isActive
                      ? t('uncategorized:deactivate_evaluation_sheet')
                      : t('uncategorized:activate_evaluation_sheet')}
                  </Button>
                )
              }
            />
          ) : groupScores == null ? (
            areScoresPending ? (
              <Loader className="m-auto flex" />
            ) : (
              <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
            )
          ) : (
            <StudentsScoresTable
              headers={headers}
              courseId={courseId}
              groupId={selectedGroup.id}
              semId={parsedSemId}
              groupName={selectedGroup.name}
              isClosedSemester={isClosedSemester}
              data={groupScores}
              cards={cards ?? []}
              onSaved={refetchScores}
            />
          )}
        </>
      )}
    </>
  )
}
